// @ts-check
/* ═══════════════════════ POST-DAG AI-REFLEKSJON (#7) ═══════════════════════
   Mot slutten av dagen kan Claude skrive en kladd til dagboken basert på
   hvor dere var, hva som sto i planen og været — i førsteperson, varmt og
   konkret. Brukeren redigerer videre selv. Senker terskelen for å faktisk
   føre dagbok hver kveld.

   Bruker /api/dest-chat (ikke-strømmende JSON). Krever nett.
   ═══════════════════════════════════════════════════════════════════════════ */

import { DAYS, DAY_INFO } from './data.js';
import { getDayWeather, formatBadge } from './weather.js';

const TRIP_START = new Date('2026-07-17T13:50:00+02:00');
const TRIP_END   = new Date('2026-08-03T15:25:00+02:00');

/** Hvilken reisedag er det nå? -1 før/etter reisen. */
export function currentTripDay(){
  const now = new Date();
  if (now < TRIP_START || now > TRIP_END) return -1;
  return Math.floor((now - TRIP_START) / 86400000);
}

/** Etter kl. 18 lokalt regner vi dagen som «ferdig nok» til refleksjon. */
export function isReflectionTime(){
  return new Date().getHours() >= 18;
}

/** Kveldsrefleksjon (N12): fra kl. 21 nudger vi til å oppsummere dagen. */
export function isEveningReflection(){
  return new Date().getHours() >= 21;
}

const SYSTEM = `Du er en varm, observant reisedagbok-medforfatter for Robert, Margita og Nicholas på deres Canada- og Alaska-tur sommeren 2026. \
Skriv en kort dagbokkladd i FØRSTEPERSON FLERTALL («vi»), på norsk (bokmål). \
3–5 setninger. Konkret og sanselig, ikke svulstig. Bygg KUN på fakta du får — ikke dikt opp aktiviteter som ikke er nevnt. \
Avslutt gjerne med én liten ting å huske eller en følelse. Ingen overskrift, ingen emoji, ingen anførselstegn — bare teksten.`;

/**
 * Be Claude skrive en dagbokkladd for en gitt reisedag.
 * @param {number} dayIdx
 * @returns {Promise<string|null>} teksten, eller null ved feil/offline
 */
export async function draftReflection(dayIdx){
  if (typeof dayIdx !== 'number' || dayIdx < 0 || !DAYS[dayIdx]) return null;
  if (!navigator.onLine) return null;

  const day = DAYS[dayIdx];
  const info = DAY_INFO[dayIdx];
  const items = (day.items || []).map(i => `${i.t ? i.t + ': ' : ''}${i.v}`).join('; ');
  const w = await getDayWeather(dayIdx).catch(() => null);

  const facts = [
    `Dag ${dayIdx + 1} (${day.d}, ${day.wd || ''}): ${day.title}.`,
    items ? `På planen: ${items}.` : '',
    day.hotel ? `Overnatting: ${day.hotel}.` : '',
    w ? `Vær: ${formatBadge(w)}, ${w.tmin}–${w.tmax}°.` : '',
    info?.trivia ? `Visste-du: ${info.trivia}` : '',
  ].filter(Boolean).join(' ');

  const userMsg = `Skriv dagbokkladden for denne dagen. Fakta:\n${facts}`;

  try {
    const r = await fetch('/api/dest-chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        system: SYSTEM,
        messages: [{ role: 'user', content: userMsg }],
      }),
    });
    if (!r.ok) return null;
    const json = await r.json();
    // /api/dest-chat returnerer { content: [{type:'text', text}], usage }
    const text = Array.isArray(json?.content)
      ? json.content.filter(b => b.type === 'text').map(b => b.text).join('').trim()
      : (json?.text || '').trim();
    return text || null;
  } catch {
    return null;
  }
}

const EVENING_SYSTEM = `Du er en varm, observant reisedagbok-medforfatter for Robert, Margita og Nicholas på deres Canada- og Alaska-tur sommeren 2026. \
Det er kveld, og dere ser tilbake på dagen som var. Skriv en kort kveldsrefleksjon i FØRSTEPERSON FLERTALL («vi»), på norsk (bokmål). \
4–6 setninger. Bygg PRIMÆRT på det vi faktisk noterte, brukte og fotograferte i dag — vev det inn naturlig. \
Hvis vi har skrevet egne notater, ta utgangspunkt i dem og utdyp varmt; ikke gjenta dem ordrett. \
Konkret og sanselig, ikke svulstig. Ikke dikt opp aktiviteter som ikke er nevnt. \
Avslutt med én liten ting å huske fra dagen. Ingen overskrift, ingen emoji, ingen anførselstegn — bare teksten.`;

/**
 * Be Claude oppsummere dagen basert på FAKTISKE data: dagbok-notater
 * skrevet i dag, penger brukt og antall bilder — i tillegg til planen.
 * Brukes av kveldsrefleksjonen (N12).
 * @param {number} dayIdx
 * @param {{ diaryEntries?: any[], budgetEntries?: any[] }} [actual]
 * @returns {Promise<string|null>} teksten, eller null ved feil/offline
 */
export async function summarizeDay(dayIdx, actual = {}){
  if (typeof dayIdx !== 'number' || dayIdx < 0 || !DAYS[dayIdx]) return null;
  if (!navigator.onLine) return null;

  const day = DAYS[dayIdx];
  const info = DAY_INFO[dayIdx];
  const items = (day.items || []).map(i => `${i.t ? i.t + ': ' : ''}${i.v}`).join('; ');
  const w = await getDayWeather(dayIdx).catch(() => null);

  const diaryEntries = Array.isArray(actual.diaryEntries) ? actual.diaryEntries : [];
  const budgetEntries = Array.isArray(actual.budgetEntries) ? actual.budgetEntries : [];

  // Faktiske dagboknotater fra i dag
  const notes = diaryEntries
    .map(e => (e?.text || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  const moods = [...new Set(diaryEntries.map(e => e?.mood).filter(Boolean))];
  const photoCount = diaryEntries.reduce((s, e) => s + (Array.isArray(e?.photos) ? e.photos.length : 0), 0);

  // Penger brukt i dag
  const spentCad = budgetEntries.reduce((s, e) => s + (Number(e?.amtCad) || 0), 0);
  const spendCats = [...new Set(budgetEntries.map(e => e?.cat).filter(Boolean))];

  const facts = [
    `Dag ${dayIdx + 1} (${day.d}, ${day.wd || ''}): ${day.title}.`,
    items ? `På planen sto: ${items}.` : '',
    day.hotel ? `Overnatting: ${day.hotel}.` : '',
    w ? `Vær: ${formatBadge(w)}, ${w.tmin}–${w.tmax}°.` : '',
    notes.length ? `Vi noterte selv: ${notes.map(n => `«${n}»`).join(' ')}` : '',
    moods.length ? `Stemning: ${moods.join(' ')}.` : '',
    photoCount ? `Vi tok ${photoCount} bilde${photoCount === 1 ? '' : 'r'}.` : '',
    spentCad > 0 ? `Vi brukte ca. ${Math.round(spentCad)} CAD${spendCats.length ? ` (${spendCats.join(', ')})` : ''}.` : '',
    info?.trivia ? `Visste-du: ${info.trivia}` : '',
  ].filter(Boolean).join(' ');

  const userMsg = `Skriv kveldsrefleksjonen for dagen vår. Fakta:\n${facts}`;

  try {
    const r = await fetch('/api/dest-chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        system: EVENING_SYSTEM,
        messages: [{ role: 'user', content: userMsg }],
      }),
    });
    if (!r.ok) return null;
    const json = await r.json();
    const text = Array.isArray(json?.content)
      ? json.content.filter(b => b.type === 'text').map(b => b.text).join('').trim()
      : (json?.text || '').trim();
    return text || null;
  } catch {
    return null;
  }
}
